import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, first, map, Observable } from 'rxjs';
import { SessionService } from '../services/session.service';
import { BlockUIService } from './blockui.service';

import { NgxSpinnerService } from 'ngx-spinner';

export interface IOptions {
  headers?: HttpHeaders | {
    [header: string]: string | string[];
  };
  observe: "response";
  context?: HttpContext;
  params?: HttpParams | {};
  reportProgress?: boolean;
  responseType?: "json";
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  private TOKEN_AUTHORIZATION = 'Oficina.WebApi.Angular4.Token';

  constructor(
    private http: HttpClient,
    private blockUIService: BlockUIService,
    private spinnerService: NgxSpinnerService,
    private sessionService: SessionService
  ) { }

  public httpGet(url: string, authorized: boolean = false) {
    this.spinnerService.show();
    return new Promise<any>((resolve, reject) => {
      this.http.get<any>(url, this.getOptions(authorized))
        .pipe(first())
        .subscribe({
          next: (response: any) => {
            resolve(this.parseResponse(response, true));
          },
          error: (error) => {
            reject(this.handleError(error, true));
          },
        });
    });
  }

  public httpPost(object: any, url: string, blocking: boolean = true, authorized: boolean = true) {
    this.blockUI(blocking);
    this.spinnerService.show();

    let body = JSON.stringify(object);

    return new Promise<any>((resolve, reject) => {
      this.http.post<any>(url, body, this.getOptions(authorized))
        .pipe(first())
        .subscribe({
          next: (response: any) => {
            resolve(this.parseResponse(response, blocking));
          },
          error: (error) => {
            reject(this.handleError(error, blocking));
          },
        });
    });
  }

  public httpPostOptions(object: any, url: string, blocking: boolean = true, authorized: boolean = true) {
    const headers = new HttpHeaders({
      'api-version': this.sessionService.version,
      'origen': 'Web',
      'Authorization': localStorage.getItem('Oficina.WebApi.Angular4.Token') || ''
    });

    this.blockUI(blocking);
    this.spinnerService.show();

    return new Promise<any>((resolve, reject) => {
      this.http.post<any>(url, object, {headers})
        .pipe(first())
        .subscribe({
          next: (response: any) => {
            resolve(this.parseResponse(response, blocking));
          },
          error: (error) => {
            reject(this.handleError(error, blocking));
          },
        });
    });
  }

  public httpPut(object: any, url: string, blocking: boolean = true) {
    this.blockUI(blocking);
    this.spinnerService.show();

    let body = JSON.stringify(object);

    return new Promise<any>((resolve, reject) => {
      this.http.put<string>(url, body, this.getOptions(true))
        .pipe(first())
        .subscribe({
          next: (response: any) => {
            resolve(this.parseResponse(response, blocking));
          },
          error: (error) => {
            reject(this.handleError(error, blocking));
          },
        });
    });
  }

  public httpPutNoAuth(object: any, url: string, blocking: boolean = true) {
    this.blockUI(blocking);
    this.spinnerService.show();

    let body = JSON.stringify(object);

    return new Promise<any>((resolve, reject) => {
      this.http.put<string>(url, body, this.getOptions(false))
        .pipe(first())
        .subscribe({
          next: (response: any) => {
            resolve(this.parseResponse(response, blocking));
          },
          error: (error) => {
            reject(this.handleError(error, blocking));
          },
        });
    });
  }

  public putDownloadFile(object: any, url: string) {
    this.spinnerService.show();
    const options = {
      headers: this.getHeaders(true),
      responseType: 'blob' as 'json'
    };
    let body = JSON.stringify(object);

    return new Promise<any>((resolve, reject) => {
      this.http.put<string>(url, body, options)
        .pipe(first())
        .subscribe({
          next: (response: any) => {
            resolve(this.parseResponseDownloadPDF(response));
          },
          error: (error) => {
            reject(this.handleError(error, true));
          },
        });
    });
  }

  public putDownloadFileNoAuth(object: any, url: string) {
    this.spinnerService.show();
    const options = {
      headers: this.getHeaders(false),
      responseType: 'blob' as 'json'
    };
    let body = JSON.stringify(object);

    return new Promise<any>((resolve, reject) => {
      this.http.put<string>(url, body, options)
        .pipe(first())
        .subscribe({
          next: (response: any) => {
            resolve(this.parseResponseDownloadPDF(response));
          },
          error: (error) => {
            reject(this.handleError(error, true));
          },
        });
    });
  }

  private parseResponseDownloadPDF(response: any) {
    this.spinnerService.hide();
    return new Blob([response], { type: 'application/pdf' });
  }

  private getOptions(auth: boolean): IOptions {
    return { observe: 'response', headers: this.getHeaders(auth) };
  };

  private getHeaders(authorize: boolean = false) {
    let obj = {};
    obj['Content-Type'] = 'application/json; charset=utf-8';
    obj['Accept'] = 'q=0.8;application/json;q=0.9;application/pdf;application/octet-stream';
    obj['api-version'] = this.sessionService.version;
    obj['origen'] = 'Web';

    if (authorize) {
      let token = localStorage.getItem(this.TOKEN_AUTHORIZATION);
      obj['Authorization'] = token;
    }
    return obj;
  }

  private handleError(error: any, blocking: Boolean) {
    this.spinnerService.hide();
    const body = error?.error ? error.error : error;
    if (blocking) {
      this.blockUIService.blockUIEvent.emit({
        value: false
      });
    }
    return body;
  }

  private parseResponse(response: any, blocking: Boolean) {
    let body = '';
    try {
      if (response.headers) {
        let authorizationToken = response.headers.get('Authorization');
        if (authorizationToken != null) {
          if (typeof (Storage) !== 'undefined') {
            localStorage.setItem(this.TOKEN_AUTHORIZATION, authorizationToken);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }

    this.spinnerService.hide();
    if (blocking) {
      this.blockUIService.blockUIEvent.emit({
        value: false
      });
    }

    if (response.body !== '') {
      body = response.body;
    }
    return body;
  }

  private blockUI(blocking: boolean = true) {
    if (blocking) {
      this.blockUIService.blockUIEvent.emit({
        value: true
      });
    }
  }
}